import * as React from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Table primitives.
 *
 * Cell padding comes from the `--row-py` / `--row-px` custom properties via
 * `.row-pad`, not from fixed classes, so the density control in the toolbar
 * moves every cell in the table by setting one `data-density` attribute on the
 * wrapper. Hard-coded `px-4 py-3` would mean threading a density prop into
 * every column renderer on every page.
 */

const TableScroll = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    // Wide tables scroll inside this box. The page itself must never scroll
    // sideways — that drags the sticky header off-screen.
    <div
      ref={ref}
      className={cn('thin-scroll relative w-full overflow-x-auto overflow-y-visible', className)}
      {...props}
    />
  ),
);
TableScroll.displayName = 'TableScroll';

const Table = React.forwardRef<HTMLTableElement, React.TableHTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table
      ref={ref}
      className={cn('w-full caption-bottom border-separate border-spacing-0 text-sm', className)}
      {...props}
    />
  ),
);
Table.displayName = 'Table';

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={cn(className)} {...props} />);
TableHeader.displayName = 'TableHeader';

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <tbody ref={ref} className={cn(className)} {...props} />);
TableBody.displayName = 'TableBody';

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement> & { selected?: boolean; clickable?: boolean }
>(({ className, selected, clickable, ...props }, ref) => (
  <tr
    ref={ref}
    data-selected={selected ? '' : undefined}
    className={cn(
      'group/row transition-colors',
      // Zebra by odd child rather than a class per row — one rule, and it
      // survives sorting and pagination without the page tracking indices.
      '[&:nth-child(even)]:bg-muted/25',
      'hover:bg-primary/[0.045]',
      selected && 'bg-primary/[0.07] hover:bg-primary/[0.09]',
      clickable && 'cursor-pointer',
      className,
    )}
    {...props}
  />
));
TableRow.displayName = 'TableRow';

/**
 * A column header. When `sort_dir` is provided the whole thing becomes a
 * button and reports `aria-sort`, which is what makes the sort state audible
 * to a screen reader rather than only visible as an arrow.
 */
const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement> & {
    sortable?: boolean;
    sort_dir?: 'asc' | 'desc' | false;
    on_sort?: () => void;
    align?: 'left' | 'right' | 'center';
    /** Sticky offset in px for the header row. */
    sticky_top?: number;
  }
>(
  (
    { className, children, sortable, sort_dir, on_sort, align = 'left', sticky_top, ...props },
    ref,
  ) => {
    const aria_sort = !sortable
      ? undefined
      : sort_dir === 'asc'
        ? 'ascending'
        : sort_dir === 'desc'
          ? 'descending'
          : 'none';
    return (
      <th
        ref={ref}
        scope="col"
        aria-sort={aria_sort}
        style={sticky_top !== undefined ? { top: sticky_top } : undefined}
        className={cn(
          'row-pad sticky z-sticky whitespace-nowrap border-b border-border bg-muted/70 text-micro font-bold uppercase tracking-wide text-muted-foreground backdrop-blur',
          // Explicit: the UA stylesheet centres `th`, so every left-aligned
          // column had its header floating in the middle of the column while
          // the cells below it started at the left edge.
          align === 'left' && 'text-left',
          align === 'right' && 'text-right',
          align === 'center' && 'text-center',
          className,
        )}
        {...props}
      >
        {sortable ? (
          <button
            type="button"
            onClick={on_sort}
            className={cn(
              'group/sort -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 uppercase transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              align === 'right' && 'flex-row-reverse',
            )}
          >
            <span>{children}</span>
            {sort_dir === 'asc' ? (
              <ArrowUp className="h-3 w-3 text-primary" />
            ) : sort_dir === 'desc' ? (
              <ArrowDown className="h-3 w-3 text-primary" />
            ) : (
              // Visible only on hover/focus: showing an idle arrow on every
              // sortable column reads as "sorted by all of them at once".
              <ChevronsUpDown className="h-3 w-3 opacity-0 transition-opacity group-hover/sort:opacity-60 group-focus-visible/sort:opacity-60" />
            )}
          </button>
        ) : (
          children
        )}
      </th>
    );
  },
);
TableHead.displayName = 'TableHead';

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & { align?: 'left' | 'right' | 'center' }
>(({ className, align = 'left', ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'row-pad border-b border-border/60 align-middle text-data',
      align === 'right' && 'text-right',
      align === 'center' && 'text-center',
      className,
    )}
    {...props}
  />
));
TableCell.displayName = 'TableCell';

export { TableScroll, Table, TableHeader, TableBody, TableRow, TableHead, TableCell };
